import { useState, useEffect } from "react";
import { Cloud, Sun, Wind, Droplets, Eye, Thermometer } from "lucide-react";
import { api } from "@/services/api";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";

const getScoreColor = (score) => {
  if (score >= 8) return "text-emerald-400";
  if (score >= 6) return "text-amber-400";
  if (score >= 4) return "text-orange-400";
  return "text-ignition-red";
};

const getScoreBg = (score) => {
  if (score >= 8) return "bg-emerald-500/10 border-emerald-500/30";
  if (score >= 6) return "bg-amber-500/10 border-amber-500/30";
  if (score >= 4) return "bg-orange-500/10 border-orange-500/30";
  return "bg-ignition-red/10 border-ignition-red/30";
};

const getScoreMessage = (quality) => {
  switch (quality) {
    case "Excellent":
      return "Perfect conditions for launch viewing! Clear skies and great visibility.";
    case "Good":
      return "Good conditions for viewing. You should have a clear view of any launches.";
    case "Fair":
      return "Decent conditions but some cloud cover may affect visibility.";
    case "Poor":
      return "Poor conditions. Clouds, rain, or wind may significantly impact viewing.";
    default:
      return "Check current conditions before heading out.";
  }
};

export default function WeatherPage({ zipCode: initialZipCode, setZipCode: setGlobalZipCode }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zipCode, setZipCode] = useState(initialZipCode || "32920");
  const [inputZip, setInputZip] = useState(initialZipCode || "32920");

  const fetchWeather = async (zip) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getWeather(zip);
      setWeather(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather(zipCode);
    // Refresh weather every 10 minutes
    const interval = setInterval(() => fetchWeather(zipCode), 600000);
    return () => clearInterval(interval);
  }, [zipCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (/^\d{5}$/.test(inputZip)) {
      setZipCode(inputZip);
      setGlobalZipCode?.(inputZip);
      localStorage.setItem("florida_zip", inputZip);
      toast.success("Location updated!");
    } else {
      toast.error("Please enter a valid 5-digit Florida zip code");
    }
  };

  return (
    <div className="min-h-screen" data-testid="weather-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        {/* Header */}
        <PageHeader 
          icon={Cloud}
          title="Weather & Viewing"
          subtitle="Current conditions and launch viewing score for your location"
        />

        {/* Zip Code Input */}
        <form onSubmit={handleSubmit} className="glass-card p-4 mb-6 flex gap-3">
          <Input
            type="text"
            placeholder="Enter Florida zip code"
            value={inputZip}
            onChange={(e) => setInputZip(e.target.value)}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
            maxLength={5}
            data-testid="weather-zip-input"
          />
          <Button type="submit" className="btn-primary" data-testid="weather-update-btn">
            Update
          </Button>
        </form>

        {error ? (
          <ErrorState message={error} onRetry={() => fetchWeather(zipCode)} />
        ) : loading ? (
          <LoadingState message="Loading weather data..." />
        ) : weather && (
          <div className="space-y-6">
            {/* Viewing Score Card */}
            <div className={`glass-card p-6 md:p-8 border-2 ${getScoreBg(weather.viewing_score)}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-widest font-heading mb-2">
                    Launch Viewing Score
                  </p>
                  <div className="flex items-baseline gap-3">
                    <span className={`text-6xl md:text-7xl font-mono font-bold ${getScoreColor(weather.viewing_score)}`}>
                      {weather.viewing_score}
                    </span>
                    <span className="text-2xl text-slate-500 font-mono">/10</span>
                  </div>
                  <p className={`text-xl font-heading font-bold mt-2 ${getScoreColor(weather.viewing_score)}`}>
                    {weather.viewing_quality}
                  </p>
                </div>
                <div className="flex-1 max-w-md">
                  <p className="text-slate-400">
                    {getScoreMessage(weather.viewing_quality)}
                  </p>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="glass-card p-4">
              <p className="text-sm text-slate-400">
                Showing weather for <span className="text-white font-semibold">{weather.location}</span> (ZIP: {weather.zip_code})
              </p>
            </div>

            {/* Current Conditions */}
            <div className="glass-card p-6">
              <h2 className="section-title mb-6">Current Conditions</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Thermometer className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Temperature</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">
                    {Math.round(weather.current.temperature)}°F
                  </p>
                  <p className="text-sm text-slate-500">
                    Feels like {Math.round(weather.current.feels_like)}°F
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Cloud className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Cloud Cover</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">
                    {weather.current.cloud_cover}%
                  </p>
                  <p className="text-sm text-slate-500">
                    {weather.current.cloud_cover < 25 ? "Clear" : weather.current.cloud_cover < 50 ? "Partly cloudy" : "Cloudy"}
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Visibility</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">
                    {Math.round(weather.current.visibility_km)} km
                  </p>
                  <p className="text-sm text-slate-500">
                    {weather.current.visibility_km > 10 ? "Excellent" : weather.current.visibility_km > 5 ? "Good" : "Poor"}
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wind className="w-5 h-5 text-cosmic-blue" strokeWidth={1.5} />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Wind</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">
                    {Math.round(weather.current.wind_speed)} mph
                  </p>
                  <p className="text-sm text-slate-500">
                    Gusts: {Math.round(weather.current.wind_gusts || 0)} mph
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Droplets className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Humidity</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">
                    {weather.current.humidity}%
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Droplets className="w-5 h-5 text-ignition-red" strokeWidth={1.5} />
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Rain</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">
                    {weather.current.rain || 0}"
                  </p>
                  <p className="text-sm text-slate-500">
                    {weather.current.rain > 0 ? "Raining" : "No rain"}
                  </p>
                </div>
              </div>
            </div>

            {/* Hourly Forecast */}
            {weather.hourly && weather.hourly.length > 0 && (
              <div className="glass-card p-6">
                <h2 className="section-title mb-4">Hourly Forecast</h2>
                <div className="overflow-x-auto">
                  <div className="flex gap-3 min-w-max pb-2">
                    {weather.hourly.map((hour) => (
                      <div key={hour.time} className="bg-white/5 rounded-xl p-4 min-w-[100px] text-center">
                        <p className="text-xs text-slate-500 mb-2">
                          {format(new Date(hour.time), "h a")}
                        </p>
                        <p className="text-xl font-mono font-bold text-white mb-1">
                          {Math.round(hour.temperature)}°
                        </p>
                        <div className="flex items-center justify-center gap-1 text-xs text-slate-400">
                          <Cloud className="w-3 h-3" strokeWidth={1.5} />
                          {hour.cloud_cover}%
                        </div>
                        {hour.precipitation_probability > 0 && (
                          <div className="flex items-center justify-center gap-1 text-xs text-blue-400 mt-1">
                            <Droplets className="w-3 h-3" strokeWidth={1.5} />
                            {hour.precipitation_probability}%
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Sun Times */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-amber-500/20">
                    <Sun className="w-6 h-6 text-amber-400" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-heading">Sunrise</p>
                    <p className="text-2xl font-mono font-bold text-white">
                      {weather.sunrise 
                        ? format(new Date(weather.sunrise), "h:mm a")
                        : "--:--"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-orange-500/20">
                    <Sun className="w-6 h-6 text-orange-400" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-heading">Sunset</p>
                    <p className="text-2xl font-mono font-bold text-white">
                      {weather.sunset 
                        ? format(new Date(weather.sunset), "h:mm a")
                        : "--:--"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
